import axios from 'axios';
import { createContext, type Dispatch, type ReactNode, type SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { type AdditionalDiscount, applyDiscountRules, type ApplyDiscountRulesResult, calculateAdditionalDiscounts, type DiscountRule, type RuleDiscount } from '@/helpers/quote';

export interface CartItem {
    uuid: string;
    sku?: string;
    type?: string;
    price: number;
    quantity: number;
    remaining?: number | null;
    tax_class?: string;
    is_pre_order?: boolean;
    discountQty?: number;
    discountPercent?: number;
    paidQty?: number;
    [key: string]: unknown;
}

/**
 * `addItem`'s input: a catalog/product-page product being added to the cart.
 * `quantity` isn't known yet — the cart always starts a new line at 1 and
 * increments an existing one, ignoring whatever the caller passes here.
 * Declared as its own interface (rather than `Omit<CartItem, 'quantity'>`)
 * because `Omit` collapses literal keys into `CartItem`'s index signature,
 * losing the named properties entirely.
 */
export interface AddableCartItem {
    uuid: string;
    sku?: string;
    type?: string;
    price: number;
    quantity?: number;
    remaining?: number | null;
    tax_class?: string;
    is_pre_order?: boolean;
    discountQty?: number;
    discountPercent?: number;
    paidQty?: number;
    [key: string]: unknown;
}

interface ServerCartItem {
    is_pre_order?: boolean;
    product: Record<string, unknown> & { uuid: string; price: number };
    quantity: number;
}

interface SyncPayloadItem {
    is_pre_order: boolean;
    product_uuid: string;
    quantity: number;
}

interface QuoteContextValue {
    items: CartItem[];
    addItem: (product: AddableCartItem) => void;
    updateQuantity: (uuid: string, quantity: number) => void;
    removeItem: (uuid: string) => void;
    clearQuote: () => void;
    setItems: Dispatch<SetStateAction<CartItem[]>>;
    totalItems: number;
    totalMerchandise: number;
    totalPrice: number;
    adjustedTotalPrice: number;
    taxableAmountsByClass: Record<string, number>;
    displayItems: CartItem[];
    ruleDiscounts: RuleDiscount[];
    additionalDiscounts: AdditionalDiscount[];
    isQuotePanelOpen: boolean;
    setQuotePanelOpen: Dispatch<SetStateAction<boolean>>;
}

const QuoteContext = createContext<QuoteContextValue | null>(null);

const CART_STORAGE_KEY = 'quote_cart';
const CART_SYNC_DEBOUNCE_MS = 500;

const readLocalCart = (): CartItem[] => {
    try {
        const saved = localStorage.getItem(CART_STORAGE_KEY);

        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
};

const flattenServerItem = ({ is_pre_order: isPreOrder, product, quantity }: ServerCartItem): CartItem => {
    return { ...product, is_pre_order: isPreOrder, quantity };
};

const toSyncPayload = (cartItems: CartItem[]): SyncPayloadItem[] => {
    return cartItems.map(({ is_pre_order: isPreOrder, quantity, uuid }) => {
        return {
            is_pre_order: Boolean(isPreOrder),
            product_uuid: uuid,
            quantity,
        };
    });
};

export function QuoteProvider({ children }: { children: ReactNode }) {
    const [
        items,
        setItems,
    ] = useState<CartItem[]>([]);

    const [
        isHydrated,
        setIsHydrated,
    ] = useState(false);

    const skipNextSyncRef = useRef(false);

    useEffect(() => {
        const controller = new AbortController();

        axios.get<ServerCartItem[]>('/api/cart', { signal: controller.signal })
            .then(({ data }) => {
                const serverItems = Array.isArray(data) ? data.map(flattenServerItem) : [];
                const local = readLocalCart();

                if (serverItems.length === 0 && local.length > 0) {
                    setItems(local);
                } else {
                    skipNextSyncRef.current = true;
                    setItems(serverItems);
                }
            })
            .catch((error: unknown) => {
                if (axios.isCancel(error)) {
                    return;
                }
                skipNextSyncRef.current = true;
                setItems(readLocalCart());
            })
            .finally(() => {
                setIsHydrated(true);
            });

        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (!isHydrated) {
            return undefined;
        }

        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));

        if (skipNextSyncRef.current) {
            skipNextSyncRef.current = false;
            return undefined;
        }

        const timer = setTimeout(() => {
            axios.put('/api/cart', { items: toSyncPayload(items) })
                .catch(() => {
                    // localStorage holds truth; next mutation retries the PUT.
                });
        }, CART_SYNC_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [
        items,
        isHydrated,
    ]);

    const [
        discountRules,
        setDiscountRules,
    ] = useState<DiscountRule[]>([]);

    const [
        isQuotePanelOpen,
        setQuotePanelOpen,
    ] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        axios.get<{ results?: DiscountRule[] }>('/api/discount-rules', { params: { is_active: true, per_page: 100 }, signal: controller.signal })
            .then(({ data }) => {
                setDiscountRules(data.results || []);
            })
            .catch((e: unknown) => {
                if (!axios.isCancel(e)) {
                    // swallow errors
                }
            });

        return () => {
            controller.abort();
        };
    }, []);

    const addItem = useCallback((product: AddableCartItem) => {
        setItems((prev) => {
            const existingIndex = prev.findIndex((item) => {
                return item.uuid === product.uuid;
            });

            if (existingIndex >= 0) {
                const updated = [
                    ...prev,
                ];
                const current = updated[existingIndex];
                const maxQty = current.remaining;

                if (!(maxQty == null) && current.quantity >= maxQty) {
                    return prev;
                }

                updated[existingIndex] = {
                    ...current,
                    quantity: current.quantity + 1,
                };
                return updated;
            }

            return [
                ...prev,
                { ...product, quantity: 1 },
            ];
        });
    }, []);

    const updateQuantity = useCallback((uuid: string, quantity: number) => {
        if (quantity < 1) {
            return;
        }
        setItems((prev) => {
            return prev.map((item) => {
                if (item.uuid !== uuid) {
                    return item;
                }
                const maxQty = item.remaining;
                const clampedQty = (maxQty == null) ? quantity : Math.min(quantity, maxQty);

                return { ...item, quantity: clampedQty };
            });
        }
        );
    }, []);

    const removeItem = useCallback((uuid: string) => {
        setItems((prev) => {
            return prev.filter((item) => {
                return item.uuid !== uuid;
            });
        });
    }, []);

    const clearQuote = useCallback(() => {
        skipNextSyncRef.current = true;
        setItems([]);
        localStorage.removeItem(CART_STORAGE_KEY);
        axios.delete('/api/cart')
            .catch(() => {
                // Best-effort: a subsequent mutation's PUT will reconcile state.
            });
    }, []);

    const { displayItems, ruleDiscounts } = useMemo<ApplyDiscountRulesResult>(() => {
        const nonMerchandiseItems = items.filter((item) => {
            return item.type !== 'Merchandise';
        });

        return applyDiscountRules(nonMerchandiseItems, discountRules);
    }, [
        items,
        discountRules,
    ]);

    const adjustedTotalPrice = useMemo(() => {
        return displayItems.reduce((sum, item) => {
            const discountQty = item.discountQty ?? 0;
            const discountPercent = item.discountPercent ?? 100;

            return sum + (item.price * item.quantity) - (discountQty * item.price * (discountPercent / 100));
        }, 0);
    }, [
        displayItems,
    ]);

    const additionalDiscounts = useMemo<AdditionalDiscount[]>(() => {
        return calculateAdditionalDiscounts(adjustedTotalPrice, discountRules);
    }, [
        adjustedTotalPrice,
        discountRules,
    ]);

    const taxableAmountsByClass = useMemo<Record<string, number>>(() => {
        return displayItems.reduce<Record<string, number>>((acc, item) => {
            const taxClass = item.tax_class ?? 'unspecified';
            const discountQty = item.discountQty ?? 0;
            const discountPercent = item.discountPercent ?? 100;
            const itemTotal = (item.price * item.quantity) - (discountQty * item.price * (discountPercent / 100));

            acc[taxClass] = (acc[taxClass] ?? 0) + itemTotal;
            return acc;
        }, {});
    }, [
        displayItems,
    ]);

    const totalItems = items.reduce((sum, item) => {
        if (item.type === 'Merchandise') {
            return sum;
        }

        return sum + item.quantity;
    }, 0);

    const totalPrice = items.reduce((sum, item) => {
        if (item.type === 'Merchandise') {
            return sum;
        }

        return sum + (item.price * item.quantity);
    }, 0);
    const totalMerchandise = items.reduce((sum, item) => {
        if (item.type !== 'Merchandise') {
            return sum;
        }

        return sum + (item.price * item.quantity);
    }, 0);

    // Memoize the value to prevent unnecessary re-renders of consumers
    const value = useMemo<QuoteContextValue>(() => {
        return {
            items,
            addItem,
            updateQuantity,
            removeItem,
            clearQuote,
            setItems,
            totalItems,
            totalMerchandise,
            totalPrice,
            adjustedTotalPrice,
            taxableAmountsByClass,
            displayItems,
            ruleDiscounts,
            additionalDiscounts,
            isQuotePanelOpen,
            setQuotePanelOpen,
        };
    }, [
        items,
        addItem,
        updateQuantity,
        removeItem,
        clearQuote,
        totalItems,
        totalMerchandise,
        totalPrice,
        adjustedTotalPrice,
        taxableAmountsByClass,
        displayItems,
        ruleDiscounts,
        additionalDiscounts,
        isQuotePanelOpen,
    ]);

    return (
        <QuoteContext.Provider value={ value }>
            {children}
        </QuoteContext.Provider>
    );
}

// This custom hook makes it easy to use the context in any component
export function useQuote(): QuoteContextValue {
    const context = useContext(QuoteContext);

    if (!context) {
        throw new Error('useQuote must be used within a QuoteProvider');
    }
    return context;
}
